
import type { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config'
import type { PropType } from 'vue'
import type { DataTableColumn, Filter, RegularFilter } from '../../../../models'
import { defineComponent } from 'vue'

import { mapGetters } from 'vuex'

import { AlignEnum, FilterTypeEnum, KpiEnum } from '../../../../enums'

import { capitalizeSentence } from '../../../../filters'

import { CONTEXT_CONSTS } from '../../../../store/context/context.consts'

import DataTable from '../../../crud/data-table.component.vue'
import KpiBar from '../../../kpi-bar.component.vue'

export default defineComponent({
  name: 'PivotsTable',
  components: {
    DataTable,
    KpiBar,
  },
  computed: {
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    }),
    columns(): DataTableColumn[] {
      const kpi = this.kpi === KpiEnum.NO_RESULTS ? 'noResults' : this.kpi
      return [
        {
          id: 'id',
          text: this.pivot.text,
        },
        {
          id: this.kpi,
          text: capitalizeSentence(this.$epb.messages.insights.analytics.kpis[kpi].valueLabel),
          align: AlignEnum.END,
        },
        {
          id: 'percentages',
          text: this.$epb.messages.insights.analytics.volume,
          width: '13rem',
          align: AlignEnum.CENTER,
        },
      ]
    },
    selectedPivotKpiKey(): string {
      return {
        [KpiEnum.QUERIES]: () => 'queryCount',
        [KpiEnum.NO_RESULTS]: () => 'noResultsCount',
        [KpiEnum.CLICKS]: () => 'clickCount',
        [KpiEnum.FINDABILITY]: () => 'findabilityCount',
        [KpiEnum.ADD2CART]: () => 'add2cartCount',
        [KpiEnum.CHECKOUT]: () => 'checkoutCount',
      }[this.kpi]()
    },
  },
  props: {
    data: {
      type: Array as PropType<
        Array<{
          id: string
          currentPeriod: Record<string, number>
          [key: string]: Record<string, number> | string
        }>
      >,
      required: true,
    },
    pivot: Object as PropType<{ id: string; text: string }>,
    kpi: String as PropType<KpiEnum>,
    compareFilterValue: String,
    loading: Boolean,
  },
  watch: {
    pivot(): void {
      this.$refs.pivotsTableWrapper.scrollLeft = 0
    },
  },
  methods: {
    getPivotValueText(pivotValue: string): string {
      const pivotFilter = Object.values(this.context.filters).find(
        (stateFilter: Filter) =>
          stateFilter.config.type === FilterTypeEnum.REGULAR &&
          ((stateFilter as RegularFilter).config.taggingId || stateFilter.config.id) ===
            this.pivot.id,
      ) as RegularFilter
      const pivotFilterValueConfig = pivotFilter.config.values.find(
        (valueConfig: RegularFilterValueConfig) =>
          (valueConfig.taggingId || valueConfig.id) === pivotValue,
      )
      if (!pivotFilterValueConfig) {
        return pivotValue
      }
      const pivotFilterValuesMessages = this.$epb.messages.filters.values[pivotFilter.config.id]
      return (
        pivotFilterValueConfig.prettyName ||
        (!!pivotFilterValuesMessages && pivotFilterValuesMessages[pivotFilterValueConfig.id]) ||
        pivotFilterValueConfig.id
      )
    },
    getPercentageChange(dataItem): number {
      const previousPeriodData = dataItem[this.compareFilterValue]
      if (previousPeriodData) {
        const kpiPreviousPeriodValue = previousPeriodData[this.selectedPivotKpiKey] || 0
        const kpiCurrentPeriodValue = dataItem.currentPeriod[this.selectedPivotKpiKey] || 0
        const increaseValue = kpiCurrentPeriodValue - kpiPreviousPeriodValue
        return Math.round((increaseValue / (kpiPreviousPeriodValue || 1)) * 100)
      } else {
        return 0
      }
    },
    getKpiBar(percentage: number, compare = false) {
      const kpisMessages = this.$epb.messages.insights.analytics.kpis
      const kpiMessage = {
        [KpiEnum.QUERIES]: () => kpisMessages[KpiEnum.QUERIES],
        [KpiEnum.NO_RESULTS]: () => kpisMessages.noResults,
        [KpiEnum.ADD2CART]: () => kpisMessages[KpiEnum.ADD2CART],
        [KpiEnum.FINDABILITY]: () => kpisMessages[KpiEnum.FINDABILITY],
        [KpiEnum.CLICKS]: () => kpisMessages[KpiEnum.CLICKS],
        [KpiEnum.CHECKOUT]: () => kpisMessages[KpiEnum.CHECKOUT],
      }[this.kpi]()
      return {
        id: compare ? 'compare' : this.kpi,
        label: capitalizeSentence(kpiMessage.valueLabel),
        percentage: percentage || 0,
      }
    },
    getPercentageText(dataItem): string {
      const percentageChange = this.getPercentageChange(dataItem)
      const sign: string = {
        0: () => '',
        1: () => '+',
        [-1]: () => '-',
      }[Math.sign(percentageChange)]()
      return `${sign}${Math.abs(percentageChange).toLocaleString(this.$epb.locale, {
        maximumFractionDigits: 2,
      })}%`
    },
  },
})
